import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptRepairItem,
  acceptSafeRepairItems,
  applyRepairRun,
  checkStatement,
  fetchMovementById,
  getRepairRun,
  listStatementRepairFlags,
  rejectRepairItem,
  undoRepairRun,
} from '../api/endpointsBackend';
import {
  DECIDABLE_RUNS, decisionsToSend, isOpen, isQuestion, shouldReread,
} from '../components/repairs/checkup/checkupModel';
import { hasOppositeReadings } from '../utils/movementAlertReasons';

/**
 * The statement, read twice, behind a movement's warning: when the movement's
 * rules warning comes with two readings of one bank row pointing opposite
 * ways, the id of the one of its statements whose flags count extra
 * readings, else null. Only such a movement asks the repairs API, and any
 * failure (no repair routes, no permission) answers null.
 */
export function useReadTwiceStatement(movement) {
  const suspect = Boolean(movement?.needs_source_rule_review) && hasOppositeReadings(movement);
  const key = suspect ? (movement.source_files || []).map((file) => file.id).join(',') : '';
  const [found, setFound] = useState({ key: '', id: null });
  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    listStatementRepairFlags()
      .then((rows) => {
        const leftovers = new Set((rows || []).filter((row) => row.leftover_rows > 0).map((row) => row.source_file_id));
        const id = key.split(',').map(Number).find((fileId) => leftovers.has(fileId)) ?? null;
        if (alive) setFound({ key, id });
      })
      .catch(() => {
        if (alive) setFound({ key, id: null });
      });
    return () => { alive = false; };
  }, [key]);
  return key && found.key === key ? found.id : null;
}

/**
 * The repair flags of every statement of the workspace, by source file id,
 * for the Statements view's chips, its "Needs a look" filter and the
 * checkup.
 *
 * `flags` stays null when the caller may not review repairs (`enabled`
 * false: no request is sent), when the API has no repair routes yet (404, an
 * API deployed after this app) or refuses (403), and when the request fails:
 * the view then shows no chips and no filter, and says nothing.
 */
export function useStatementRepairFlags(enabled) {
  const [flags, setFlags] = useState(null);

  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve(null);
    return listStatementRepairFlags()
      .then((rows) => {
        const byFile = Object.fromEntries((rows || []).map((row) => [row.source_file_id, row]));
        setFlags(byFile);
        return byFile;
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status !== 404 && status !== 403) console.error('Error loading statement flags:', err);
        setFlags(null);
        return null;
      });
  }, [enabled]);

  useEffect(() => { reload(); }, [reload]);

  const attentionCount = useMemo(
    () => Object.values(flags || {}).filter((row) => row.needs_attention).length,
    [flags],
  );

  return { flags, attentionCount, reload };
}

// Which check a statement opens with: its latest one when that is still
// open, or applied and nothing is left to look at; else a new check.
function reusable(latest, flags) {
  if (!latest) return false;
  if (DECIDABLE_RUNS.includes(latest.status)) return true;
  return latest.status === 'applied' && !flags.needs_attention;
}

/**
 * One statement's check in the checkup: its latest check when it can be
 * reused, else a new one (read again first when the reading itself is in
 * doubt, see `shouldReread`). `phase` is `loading`, `reading` (a re-read,
 * which takes a while), `ready` or `error`.
 *
 * `apply(choices)` sends the decisions the review holds (`decisionsToSend`)
 * and applies the run; `undo()` reverses an applied run; `recheck()` starts
 * a new check. Each throws, so the page can say why in a toast.
 */
export function useStatementCheck(sourceFileId, flags) {
  const [run, setRun] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);
  const started = useRef(null);

  const check = useCallback(async (statementFlags) => {
    const reread = shouldReread(statementFlags);
    setPhase(reread ? 'reading' : 'loading');
    return checkStatement(sourceFileId, reread);
  }, [sourceFileId]);

  const begin = useCallback(async (statementFlags, { fresh = false } = {}) => {
    setError(null);
    setRun(null);
    try {
      let detail = null;
      if (!fresh && reusable(statementFlags.latest_run, statementFlags)) {
        setPhase('loading');
        detail = await getRepairRun(statementFlags.latest_run.id);
        // An open check that did not read the statement again says nothing
        // about how today's reader reads it: read it again instead.
        const open = DECIDABLE_RUNS.includes(detail.status);
        if (open && shouldReread(statementFlags) && !detail.statement_diff?.reread) detail = null;
      }
      setRun(detail || await check(statementFlags));
      setPhase('ready');
    } catch (err) {
      setError(err);
      setPhase('error');
    }
  }, [check]);

  useEffect(() => {
    if (!sourceFileId || !flags || started.current === sourceFileId) return;
    started.current = sourceFileId;
    begin(flags);
  }, [sourceFileId, flags, begin]);

  // An answer without the diff keeps the one the check came with.
  const show = useCallback((detail) => {
    setRun((prev) => (prev?.id === detail.id && !detail.statement_diff
      ? { ...detail, statement_diff: prev.statement_diff }
      : detail));
  }, []);

  const apply = useCallback(async (choices) => {
    const { accept, reject } = decisionsToSend(run.items, choices);
    // Every safe proposal chosen: one call accepts them all.
    const safe = run.items.filter((item) => item.status === 'pending' && item.safe && isOpen(item) && !isQuestion(item));
    const allSafe = safe.length > 1 && safe.every((item) => accept.includes(item.id));
    if (allSafe) await acceptSafeRepairItems(run.id);
    const covered = new Set(allSafe ? safe.map((item) => item.id) : []);
    for (const itemId of accept.filter((id) => !covered.has(id))) await acceptRepairItem(run.id, itemId);
    for (const itemId of reject) await rejectRepairItem(run.id, itemId);
    const detail = await applyRepairRun(run.id);
    show(detail);
    return detail.outcome;
  }, [run, show]);

  const undo = useCallback(async () => {
    const answer = await undoRepairRun(run.id);
    show(await getRepairRun(run.id));
    return answer.outcome;
  }, [run, show]);

  const recheck = useCallback((statementFlags) => begin(statementFlags, { fresh: true }), [begin]);

  return { run, phase, error, apply, undo, recheck };
}

/**
 * The movements a review names, read through the Movements API (their note,
 * category and accounts), by id. Asked once each; one that cannot be read
 * (deleted, folded into another) maps to null and the row falls back to the
 * facts the check carries.
 */
export function useMovementDetails(ids) {
  const [details, setDetails] = useState({});
  const asked = useRef(new Set());
  const mounted = useRef(true);
  // Set on every mount: React's development double mount runs the cleanup
  // once before the component is really gone.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const key = [...new Set(ids.filter((id) => id != null))].sort((a, b) => a - b).join(',');
  useEffect(() => {
    const todo = key ? key.split(',').map(Number).filter((id) => !asked.current.has(id)) : [];
    if (!todo.length) return;
    todo.forEach((id) => asked.current.add(id));
    Promise.all(todo.map((id) => fetchMovementById(id).then((found) => [id, found]).catch(() => [id, null])))
      .then((entries) => {
        if (mounted.current) setDetails((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      });
  }, [key]);

  return details;
}
